import type { ID } from '@octopus-do/octopus-project-core'
import TimeAgo from 'javascript-time-ago'
import type { MouseEvent } from 'react'
import type { DraggableData } from 'react-draggable'

import type { Comment, Recipient } from '@/types'
import type { Offset } from '@/utils/offset'
import { COMMENTABLE_TARGET } from './constants'

export const buildMap = (users: Recipient[]) =>
  users.reduce(
    (acc, user) => {
      acc[user.id] = user
      return acc
    },
    {} as Record<string, Recipient>
  )

const isCommentableTarget = (target: Element | null) =>
  !!target?.getAttribute(COMMENTABLE_TARGET)

export const getLocalPosition = (
  { x, y }: Offset,
  rect: DOMRect,
  scale: number,
  shift: Offset
): Offset => ({
  x: (x - rect.left - rect.width / 2) / scale - shift.x,
  y: (y - rect.top) / scale - shift.y,
})

// the inverse of getLocalPosition: where the local offset is on screen now
export const getViewportPosition = (
  { x, y }: Offset,
  rect: DOMRect,
  scale: number,
  shift: Offset
): Offset => ({
  x: rect.left + rect.width / 2 + (x + shift.x) * scale,
  y: rect.top + (y + shift.y) * scale,
})

export const findCommentableElement = (id: ID) =>
  document.querySelector(`[${COMMENTABLE_TARGET}="${id}"]`)

const findCommentableAt = ({ x, y }: Offset) =>
  document.elementsFromPoint(x, y).find(isCommentableTarget)

export const getCommentableId = (offset: Offset): ID | null =>
  findCommentableAt(offset)?.getAttribute(COMMENTABLE_TARGET) ?? null

export const getCommentableRect = (
  offset: Offset,
  defaultEl: Element
): [ID, DOMRect] => {
  const target = findCommentableAt(offset) || defaultEl

  const targetId = target.getAttribute(COMMENTABLE_TARGET) as ID

  return [targetId, target.getBoundingClientRect()]
}

export const getDraggableShift = (ev: MouseEvent, { node }: DraggableData) => {
  const rect = node.getBoundingClientRect()

  return {
    x: ev.clientX - rect.x,
    y: ev.clientY - rect.y,
  }
}

export const getEventOffset = (ev: MouseEvent): Offset => ({
  x: ev.clientX,
  y: ev.clientY,
})

const takeUniqueAuthors = (authorId: ID | null, comments: Comment[]) => {
  const unique = comments.reduce(
    (accum, current) => {
      if (accum.set.has(current.owner)) {
        return accum
      }

      accum.arr.push(current.owner)
      accum.set.add(current.owner)

      return accum
    },
    {
      arr: [authorId],
      set: new Set([authorId]),
    }
  )

  return unique.arr
}

export const takeAvatars = (
  authorId: ID | null,
  comments: Comment[],
  users: Record<string, Recipient>
) => {
  const authors = takeUniqueAuthors(authorId, comments)

  const commentators =
    authors.length <= 3 ? authors : [authors[0], authors[1], authors.at(-1)]

  return commentators.map((id) => (id && users[id]) || null)
}

export const timeAgo = (datetime: string | number): string => {
  const timeAgo = new TimeAgo('en-US')
  const date = new Date(datetime)
  return timeAgo.format(date, 'mini') as string
}
